import { makeid } from 'utils/helpers/makeid';

import ScriptWrapper from './script-wrapper';
import { addField, formatList, hasField, mapValuesToText } from '../helper';

import type { BlurbContext, ITemplateProps } from '../types';
import type {
  ExtractStepValues,
  TFormTemplateItem,
} from '../../../molecules/base-form-template/types';

export const createClinicCallTemplate = (data: ITemplateProps) => {
  const { userFullname, patientFullname } = data;
  return [
    {
      key: makeid(10),
      name: 'greetingCallContext',
      type: 'checkbox',
      title: <p>Greeting & Call Context</p>,
      description: (
        <ScriptWrapper>
          "Hello, this is {userFullname} calling from Tellihealth."
          <br />
          "I'm calling regarding a mutual patient to report a care-related need
          or issue."
          <br />
          "For awareness, this call may be recorded for quality and compliance
          purposes."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Clinic contacted',
          value: 'clinicContacted',
        },
        {
          key: makeid(10),
          label: 'Call purpose stated (patient-related care need or issue)',
          value: 'callPurposeStated',
        },
        {
          key: makeid(10),
          label: 'Recorded line disclosure provided',
          value: 'recordedLineDisclosureProvided',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'reasonForCallPatientNeedOrIssue',
      type: 'radio',
      title: <p>Reason for Call / Patient Need or Issue</p>,
      description: (
        <ScriptWrapper>
          "I'm calling regarding {patientFullname}, who is enrolled in Remote
          Patient Monitoring with us."
          <br />
          "The reason for this call is to report the following concern or need:"
          <br />
          <br />
          (Select and state the applicable scenario.)
          <br />
          <br />
          <ul>
            <li>
              • Critical escalation:
              <br />
              "We identified a concern through RPM monitoring that requires
              clinic awareness and follow-up."
            </li>
            <li>
              • Medication-related need:
              <br />
              "The patient requires assistance related to medications, such as
              refill coordination or medication support."
            </li>
            <li>
              • Appointment scheduling or follow-up need:
              <br />
              "The patient requires assistance with appointment scheduling or
              follow-up."
            </li>
            <li>
              • Social support or referral coordination:
              <br />
              "There is a need for social support resources or referral
              coordination."
            </li>
            <li>
              • Other care-related need:
              <br />
              "An additional patient-related need has been identified and
              requires clinic awareness."
            </li>
          </ul>
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Critical escalation',
          value: 'criticalEscalation',
        },
        {
          key: makeid(10),
          label: 'Medication-related assistance',
          value: 'medicationRelatedAssistance',
        },
        {
          key: makeid(10),
          label: 'Appointment scheduling or follow-up coordination',
          value: 'appointmentSchedulingOrFollowUpCoordination',
        },
        {
          key: makeid(10),
          label: 'Social support or referral coordination',
          value: 'socialSupportOrReferralCoordination',
        },
        {
          key: makeid(10),
          label: 'Other care-related need',
          value: 'otherCareRelatedNeed',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'relevantDetailsShared',
      type: 'checkbox',
      title: <p>Relevant Details Provided</p>,
      description: (
        <ScriptWrapper>
          "Relevant details include:"
          <br />
          (Share pertinent RPM data trends, patient-reported concerns,
          timelines, urgency, and any actions already taken.)
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'RPM trends shared',
          value: 'rpmTrendsShared',
        },
        {
          key: makeid(10),
          label: 'Patient-reported concerns shared',
          value: 'patientReportedConcernsShared',
        },
        {
          key: makeid(10),
          label: 'Timeline provided',
          value: 'timelineProvided',
        },
        {
          key: makeid(10),
          label: 'Urgency level communicated',
          value: 'urgencyLevelCommunicated',
        },
        {
          key: makeid(10),
          label: 'Actions already taken by Tellihealth shared',
          value: 'actionsAlreadyTakenByTellihealthShared',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'requestedActionCoordination',
      type: 'checkbox',
      title: <p>Requested Action / Coordination</p>,
      description: (
        <ScriptWrapper>
          "We're notifying your team so the clinic can determine appropriate
          next steps."
          <br />
          "Please let us know if additional information is needed from our
          side."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Clinic notified for awareness',
          value: 'clinicNotifiedForAwareness',
        },
        {
          key: makeid(10),
          label: 'Clinic to determine next steps',
          value: 'clinicToDetermineNextSteps',
        },
        {
          key: makeid(10),
          label: 'Additional information requested by clinic',
          value: 'additionalInformationRequestedByClinic',
        },
        {
          key: makeid(10),
          label: 'No additional information requested',
          value: 'noAdditionalInformationRequested',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'closingAdditionalQuestions',
      type: 'radio',
      title: <p>Closing - Additional Questions</p>,
      description: (
        <ScriptWrapper>
          “Before we disconnect, do you have any questions about the information
          I’ve shared?”
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Had no additional questions',
          value: 'noAdditionalQuestions',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: <ScriptWrapper>"Great — thank you."</ScriptWrapper>,
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Asked questions — addressed during call',
          value: 'hasAdditionalQuestions',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>"Does that answer your question?"</ScriptWrapper>
              ),
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: '_empty',
      type: 'empty',
      title: <p>Final close</p>,
      description: (
        <ScriptWrapper>
          "Thank you for your time and for partnering with us in the patient's
          care."
        </ScriptWrapper>
      ),
    },
  ] as const satisfies TFormTemplateItem[];
};

export type TClinicCallTemplateValues = ExtractStepValues<
  ReturnType<typeof createClinicCallTemplate>
>;

export const createClinicCallBlurb = (
  data: TClinicCallTemplateValues,
  ctx: BlurbContext,
) => {
  const {
    greetingCallContext,
    reasonForCallPatientNeedOrIssue,
    relevantDetailsShared,
    requestedActionCoordination,
    closingAdditionalQuestions,
  } = data;

  const txt: string[] = [];

  if (greetingCallContext?.length && !hasField(ctx, 'greetingCallContext')) {
    addField(ctx, 'greetingCallContext');

    const map = {
      clinicContacted: 'outbound call placed to partnered clinic',
      callPurposeStated:
        'call purpose stated to report a patient-related care need or issue',
      recordedLineDisclosureProvided: 'recorded line disclosure provided',
    };
    const items = mapValuesToText(greetingCallContext, map);

    if (items.length) {
      txt.push(`${formatList(items)}.`);
    }
  }

  if (
    reasonForCallPatientNeedOrIssue &&
    !hasField(ctx, 'reasonForCallPatientNeedOrIssue')
  ) {
    addField(ctx, 'reasonForCallPatientNeedOrIssue');

    const map = {
      criticalEscalation: 'critical escalation',
      medicationRelatedAssistance: 'medication assistance',
      appointmentSchedulingOrFollowUpCoordination:
        'appointment coordination or follow-up',
      socialSupportOrReferralCoordination:
        'social support or referral coordination',
      otherCareRelatedNeed: 'other care-related need',
    };
    txt.push(
      `Clinic contacted to report patient-related need: ${map[reasonForCallPatientNeedOrIssue]}.`,
    );
  }

  if (
    relevantDetailsShared?.length &&
    !hasField(ctx, 'relevantDetailsShared')
  ) {
    addField(ctx, 'relevantDetailsShared');

    const map = {
      rpmTrendsShared: 'RPM trends',
      patientReportedConcernsShared: 'patient-reported concerns',
      timelineProvided: 'timeline',
      urgencyLevelCommunicated: 'urgency level',
      actionsAlreadyTakenByTellihealthShared: 'actions already taken',
    };
    const items = mapValuesToText(relevantDetailsShared, map);

    if (items.length) {
      txt.push(
        `Relevant patient details shared with clinic, including ${formatList(
          items,
        )}.`,
      );
    }
  }

  if (
    requestedActionCoordination?.length &&
    !hasField(ctx, 'requestedActionCoordination')
  ) {
    addField(ctx, 'requestedActionCoordination');

    const map = {
      clinicNotifiedForAwareness: 'clinic notified for awareness',
      clinicToDetermineNextSteps: 'clinic to determine next steps',
      additionalInformationRequestedByClinic:
        'additional information requested by clinic',
      noAdditionalInformationRequested: 'no additional information requested',
    };
    const items = mapValuesToText(requestedActionCoordination, map);

    if (items.length) {
      txt.push(`Coordination at time of call: ${formatList(items)}.`);
    }
  }

  if (closingAdditionalQuestions && !hasField(ctx, 'closingClinicCall')) {
    addField(ctx, 'closingClinicCall');

    const map = {
      noAdditionalQuestions: 'clinic had no additional questions',
      hasAdditionalQuestions:
        'clinic asked questions which were addressed during the call',
    };
    txt.push(`Call concluded: ${map[closingAdditionalQuestions]}.`);
  }

  return txt.join(' ');
};
